'use client';

/**
 * Swagger UI embed block — renders interactive API documentation from
 * an OpenAPI spec URL. Thin wrapper around {@code swagger-ui-react}.
 *
 * <p>Dropped into the {@code /docs} page by the Default template; can be
 * placed anywhere an operator wants to surface the public API explorer.
 *
 * <h3>Why a block?</h3>
 *
 * The portal renderer is server-driven and pluggable, so even
 * commodity surfaces like API docs need to be expressible as a slot
 * fill. This block keeps the dependency on {@code swagger-ui-react}
 * isolated — only pages that actually mount {@code SwaggerEmbed} pull
 * the library into the bundle.
 *
 * <h3>Props</h3>
 *
 * <ul>
 *   <li>{@code specUrl} — absolute or app-relative URL to the
 *       OpenAPI JSON. Default {@code /v1/openapi.json}.</li>
 *   <li>{@code title} / {@code subtitle} — optional section heading
 *       above the explorer.</li>
 *   <li>{@code docExpansion} — Swagger UI's expansion strategy:
 *       {@code list} (default), {@code full}, or {@code none}.</li>
 *   <li>{@code deepLinking} — sync hash with operation id; default
 *       {@code true}.</li>
 *   <li>{@code tryItOutEnabled} — show the "Try it out" button;
 *       default {@code true}.</li>
 * </ul>
 */
import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionShell } from '../_shared/SectionShell';

// Swagger UI is heavy + DOM-dependent — lazy-load so SSR doesn't try
// to render it. The runtime mounts blocks client-side anyway when
// {@code 'use client'} is set, but lazy() keeps the chunk out of the
// initial bundle for pages that don't include this block.
const SwaggerUI = lazy(() => import('swagger-ui-react' as string));

type Localized = string | Record<string, string>;

export interface SwaggerEmbedProps {
  /** OpenAPI spec URL. Default {@code /v1/openapi.json}. */
  specUrl?: string;
  title?: Localized;
  subtitle?: Localized;
  /** {@code list} | {@code full} | {@code none}. Default {@code list}. */
  docExpansion?: 'list' | 'full' | 'none';
  deepLinking?: boolean;
  tryItOutEnabled?: boolean;
}

const DEFAULT_SPEC_URL = '/v1/openapi.json';

export function SwaggerEmbed({
  specUrl,
  title,
  subtitle,
  docExpansion = 'list',
  deepLinking = true,
  tryItOutEnabled = true,
}: SwaggerEmbedProps) {
  const { t } = useTranslation('site-builder-blocks');
  const url = (specUrl ?? '').trim() || DEFAULT_SPEC_URL;

  return (
    <SectionShell
      title={typeof title === 'string' || title === undefined ? title : undefined}
      subtitle={typeof subtitle === 'string' ? subtitle : undefined}
    >
      {/* Swagger UI ships its own stylesheet; consumers must import
          'swagger-ui-react/swagger-ui.css' once at the portal root. We
          don't import it here to avoid duplicating the stylesheet
          across bundles. */}
      <div
        style={{
          background: 'var(--color-surface-card, #fff)',
          border: '1px solid var(--color-border-subtle, rgba(0,0,0,0.06))',
          borderRadius: 'var(--radius-lg, 12px)',
          padding: 'var(--space-4, 16px)',
        }}
      >
        <Suspense
          fallback={
            <div
              style={{
                padding: 'var(--space-8, 32px)',
                color: 'var(--color-text-secondary, #64748b)',
                textAlign: 'center',
              }}
            >
              {t('docs.swagger.loading')}
            </div>
          }
        >
          <SwaggerUI
            url={url}
            docExpansion={docExpansion}
            deepLinking={deepLinking}
            tryItOutEnabled={tryItOutEnabled}
          />
        </Suspense>
      </div>
    </SectionShell>
  );
}
